import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useActiveOrg } from "@/contexts/ActiveOrgContext";
import { useViewMode } from "@/contexts/ViewModeContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Home } from "lucide-react";

const ROUTE_LABELS: Record<string, string> = {
  "/dashboard": "Tableau de bord",
  "/modules": "Modules",
  "/dossiers": "Dossiers",
  "/etats-financiers": "États financiers",
  "/verification": "Vérification",
  "/pointage-dgi": "Pointage DGI",
  "/consolidation": "Consolidation",
  "/exports": "Exports",
  "/archives": "Archives",
  "/settings/profile": "Mon profil",
  "/settings/organization": "Organisation",
  "/admin/licences": "Licences",
  "/admin/audit-logs": "Journaux d'audit",
};

export function AppBreadcrumb() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { activeOrg } = useActiveOrg();
  const { viewMode } = useViewMode();

  const orgName = activeOrg?.organization?.name;
  const isDossierWorkspace = location.pathname.startsWith("/dossiers/") && params.id;
  const isEntreprise = viewMode === "entreprise";
  const dossiersLabel = isEntreprise ? "Exercices" : "Dossiers";

  const { data: dossier } = useQuery({
    queryKey: ["dossier-breadcrumb", params.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("dossiers")
        .select("name")
        .eq("id", params.id!)
        .single();
      return data;
    },
    enabled: !!isDossierWorkspace,
    staleTime: 60_000,
  });

  // Build crumbs
  const crumbs: { label: string; href?: string }[] = [];

  // Hub
  crumbs.push({ label: "Hub", href: "/hub" });

  // Org name
  if (orgName) {
    crumbs.push({ label: orgName, href: "/dashboard" });
  }

  // Current page
  if (isDossierWorkspace) {
    crumbs.push({ label: dossiersLabel, href: "/dossiers" });
    crumbs.push({ label: dossier?.name || (isEntreprise ? "Exercice" : "Dossier") });
  } else {
    const base = location.pathname;
    const label = base === "/dossiers" ? dossiersLabel : ROUTE_LABELS[base];
    if (label) {
      crumbs.push({ label });
    }
  }

  if (crumbs.length <= 1) return null;

  return (
    <Breadcrumb className="px-4 py-2 border-b border-border bg-muted/30">
      <BreadcrumbList>
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <span key={i} className="contents">
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    className="cursor-pointer"
                    onClick={() => crumb.href && navigate(crumb.href)}
                  >
                    {i === 0 ? (
                      <span className="flex items-center gap-1.5">
                        <Home className="w-3.5 h-3.5" />
                        {crumb.label}
                      </span>
                    ) : (
                      crumb.label
                    )}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </span>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
